/*eslint-disable*/
import moment from 'moment';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

// @mui/material components
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TablePagination from '@mui/material/TablePagination';
import { makeStyles } from '@mui/styles';

// material ui icons
import SearchResultsIcon from '@mui/icons-material/Reorder';
import SearchIcon from '@mui/icons-material/Search';
import ViewIcon from '@mui/icons-material/Visibility';
import NoViewIcon from '@mui/icons-material/VisibilityOff';

// core components
import DatePicker from 'components/CustomInput/CalendarInput.js';
import CustomInput from 'components/CustomInput/CustomInput.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';

import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardIcon from 'components/Card/CardIcon.js';
import Button from 'components/CustomButtons/Button.js';
import Table from 'components/Table/Table.js';

import Receipt from './Receipt';

// style for this view
import styles from 'assets/jss/material-dashboard-pro-react/views/clientEditStyle.js';

// Redux

import { getCurrencyFormat } from '../../helpers/currency';
import { useGetTenantQuery } from 'api/tenant.api';

import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import { ThemedRadio } from '@themes/defaultThemeProps';
import {
  useGetLocationsQuery,
  useLazyGetWorkstationsQuery,
} from 'api/location.api';
import {
  useLazyGetReceiptQuery,
  useLazySearchTransactionsQuery,
} from 'api/session.api';
import { getStackTrace, log } from '@src/Log.init';

const useStyles = makeStyles(styles);

export default function SearchCriteria(props) {
  const dispatch = useDispatch();
  const { data: tenant } = useGetTenantQuery();

  const [
    triggerSearchTransactions,
    { data: searchData, isFetching, isSuccess },
  ] = useLazySearchTransactionsQuery();
  const [triggerGetReceipt, { data: currentReceipt }] =
    useLazyGetReceiptQuery();

  const { data: locationsData } = useGetLocationsQuery({ limit: 20 });
  const locations = locationsData?.results || [];
  const [
    triggerGetWorkstations,
    { data: workstations, isSuccess: isWorkstationSuccess },
  ] = useLazyGetWorkstationsQuery();
  const transactions = searchData?.data || [];
  const receiptStatus = isFetching
    ? 'pending'
    : isSuccess
    ? 'success'
    : 'failure';
  const resultCount = searchData?.metadata[0]?.total || 0;

  const classes = useStyles();

  const [dirtyData, setDirtyData] = useState(false);
  const [search, setSearch] = useState(false);
  const [viewReceipt, setViewReceipt] = useState(false);
  const [serviceType, setServiceType] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [transactionType, setTransactionType] = useState('');
  const [transactionTypeState, setTransactionTypeState] = useState('');
  const [reference, setReference] = useState('');
  const [referenceState, setReferenceState] = useState('');
  const [referenceError, setReferenceError] = useState('');
  const [mistId, setMistId] = useState('');
  const [mistIdState, setMistIdState] = useState('');
  const [mistIdError, setMistIdError] = useState('');
  const [location, setLocation] = useState('');
  const [locationState, setLocationState] = useState('');
  const [workstation, setWorkstation] = useState('');
  const [workstationState, setWorkstationState] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startDateState, setStartDateState] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endDateState, setEndDateState] = useState('');
  const [userId, setUserId] = useState('');
  const [userIdState, setUserIdState] = useState('');
  const [userIdError, setUserIdError] = useState('');
  const [sessionState, setSessionState] = useState('completed');

  const isEndDateValid = function (current) {
    const start = moment(startDate);
    if (startDate) return current.isAfter(start);
    else return true;
  };
  const isStartDateValid = function (current) {
    const end = moment(endDate);
    if (endDate) return current.isBefore(end);
    else return true;
  };

  // function that verifies if a string has a given length or not
  const verifyLength = (value, original, length, required = true) => {
    if (value && value.length >= length) {
      if (value !== original) setDirtyData(true);
      return true;
    } else if (!required && (!value || value.length === 0)) {
      if (value !== original) setDirtyData(true);
      return true;
    }
    return false;
  };

  const onClickSearch = async () => {
    setSearch(true);
    await searchTransactions();
  };
  const searchTransactions = async (pg, rows) => {
    const searchCriteria = {};
    if (sessionState) {
      searchCriteria.expiredSession = sessionState === 'expired' ? true : false;
      searchCriteria.completedSession =
        sessionState === 'completed' ? true : false;
      searchCriteria.unreconciledSession =
        sessionState === 'unreconciled' ? true : false;
    }
    if (reference && reference !== '') searchCriteria.referenceId = reference;
    if (mistId && mistId !== '') searchCriteria.mistId = mistId;
    if (location && location !== '') searchCriteria.location_id = location;
    if (startDate && startDate !== '') {
      searchCriteria.beginDate =
        startDate instanceof Date ? startDate.toISOString() : startDate;
    }
    if (endDate && endDate !== '') {
      searchCriteria.endDate =
        endDate instanceof Date ? endDate.toISOString() : endDate;
    }
    if (userId && userId !== '') searchCriteria.user = userId;
    if (transactionType && transactionType !== '')
      searchCriteria.service = transactionType;
    if (workstation && workstation !== '')
      searchCriteria.workstation = workstation;
    searchCriteria.rowsPerPage = rows ? rows : rowsPerPage;
    searchCriteria.page = pg ? pg : page;
    await triggerSearchTransactions({ criteria: searchCriteria }).unwrap();
    setDirtyData(false);
  };

  const handleChangePage = async (event, newPage) => {
    setPage(newPage + 1);
    setDirtyData(true);
    await searchTransactions(newPage + 1);
  };

  const handleChangeRowsPerPage = async (event) => {
    const rowsPP = parseInt(event.target.value, 10);
    setRowsPerPage(rowsPP);
    setPage(1);
    setDirtyData(true);
    await searchTransactions(1, rowsPP);
  };

  const locationList = () => {
    const locs = locations
      .filter((loc) => loc.status === 'active')
      .map((location, key) => {
        const option = (
          <MenuItem key={key} value={location.key}>
            {location.key + ', ' + location.name}
          </MenuItem>
        );
        return option;
      });
    locs.unshift(
      <MenuItem key={-1} value=''>
        -Empty-
      </MenuItem>
    );
    return locs;
  };

  const workstationList = () => {
    if (!isWorkstationSuccess || !workstations) {
      return [];
    }
    const locs = workstations
      .filter((wrkstn) => wrkstn.status === 'Active')
      .map((workstation, key) => {
        const option = (
          <MenuItem key={key} value={workstation.workstation_id}>
            {workstation.workstation_id + ', ' + workstation.description}
          </MenuItem>
        );
        return option;
      });
    locs.unshift(
      <MenuItem key={-1} value=''>
        -Empty-
      </MenuItem>
    );
    return locs;
  };

  const sessionStateChangeHandler = (event) => {
    if (event.target.value !== sessionState) {
      setSessionState(event.target.value);
      setDirtyData(true);
    }
  };

  const locationChangeHandler = (event) => {
    if (event.target.value !== location) {
      if (event.target.value !== '')
        triggerGetWorkstations(event.target.value)
          .unwrap()
          .catch((error) =>
            log.error({
              message_type: 'SystemError',
              message: `Error retrieving workstations for location ${event.target.value}: ${error.message}`,
              meta_data: getStackTrace(),
            })
          );
      setWorkstation(''); // any value for workstation is tied to the previous location
      setLocation(event.target.value);
      setLocationState('success');
      setDirtyData(true);
    }
  };
  const workstationChangeHandler = (event) => {
    if (event.target.value !== workstation) {
      setWorkstation(event.target.value);
      setWorkstationState('success');
      setDirtyData(true);
    }
  };
  const transactionTypeChangeHandler = (event) => {
    if (event.target.value !== transactionType) {
      setTransactionType(event.target.value);
      setTransactionTypeState('success');
      setDirtyData(true);
    }
  };
  const handleViewTransaction = async (index) => {
    await triggerGetReceipt({ sessionId: transactions[index]._id }).unwrap();
    setServiceType(transactions[index].service);
    setViewReceipt(true);
  };

  const actionButton = (Icon, color, handler, key, active) => {
    return (
      <Button
        color={color}
        simple
        className={classes.actionButton}
        onClick={() => handler(key)}
        disabled={!active}
      >
        <Icon className={classes.icon} />
      </Button>
    );
  };
  const searchResults = () => {
    let results = [];

    if (transactions)
      results = transactions.map((transaction, key) => {
        var result = [];
        var actionButtons = [];

        let serviceType = 'Payment';
        if (transaction.service === 'refund') serviceType = 'Refund';
        if (transaction.service === 'cashmanagement')
          serviceType = 'Cash Management';
        result.push(transaction.reference);
        result.push(serviceType);
        result.push(transaction.location_id);
        result.push(transaction.workstation_id);
        result.push(transaction.user_id);
        result.push(
          transaction.total_gross
            ? transaction.total_gross.toFormat(getCurrencyFormat(tenant?.base_currency))
            : 'null'
        );
        result.push(new Date(transaction.created_at).toLocaleString());
        if (['Paid', 'Refunded'].includes(transaction.status))
          actionButtons.push(
            actionButton(ViewIcon, 'warning', handleViewTransaction, key, true)
          );
        else
          actionButtons.push(
            actionButton(
              NoViewIcon,
              'warning',
              handleViewTransaction,
              key,
              false
            )
          );
        result.push(actionButtons);
        return result;
      });
    return results;
  };

  const handleCloseReceipt = () => {
    setViewReceipt(false);
  };

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color='rose' icon>
            <CardIcon color='rose'>
              <SearchIcon />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Search Criteria</h4>
          </CardHeader>
          <CardBody>
            <GridContainer>
              <GridItem xs={6}>
                <FormControl
                  variant='standard'
                  className={classes.formControl}
                  fullWidth={true}
                >
                  <InputLabel
                    id='location-select-label'
                    className={classes.inputLabel}
                  >
                    Location
                  </InputLabel>
                  <Select
                    variant='standard'
                    className={classes.select}
                    labelId='location-select-label'
                    id='location-select'
                    value={location}
                    onChange={(event) => locationChangeHandler(event)}
                  >
                    {locationList()}
                  </Select>
                </FormControl>
                <FormControl
                  variant='standard'
                  className={classes.formControl}
                  fullWidth={true}
                >
                  <InputLabel
                    id='workstation-select-label'
                    className={classes.inputLabel}
                  >
                    Workstation
                  </InputLabel>
                  <Select
                    variant='standard'
                    className={classes.select}
                    labelId='workstation-select-label'
                    id='workstation-select'
                    value={workstation}
                    onChange={(event) => workstationChangeHandler(event)}
                  >
                    {workstationList()}
                  </Select>
                </FormControl>
                <FormControl
                  variant='standard'
                  className={classes.formControl}
                  fullWidth={true}
                >
                  <InputLabel
                    id='status-select-label'
                    className={classes.inputLabel}
                  >
                    Transaction Types
                  </InputLabel>
                  <Select
                    variant='standard'
                    className={classes.select}
                    labelId='transaction-select-label'
                    id='transaction-select'
                    value={transactionType}
                    onChange={(event) => transactionTypeChangeHandler(event)}
                  >
                    <MenuItem value=''>-Empty-</MenuItem>
                    <MenuItem value='payment'>Payment</MenuItem>
                    <MenuItem value='refund'>Refund</MenuItem>
                  </Select>
                </FormControl>
                <GridContainer>
                  <GridItem xs={6}>
                    <DatePicker
                      isValidDate={(current) => {
                        return endDate ? current.isBefore(endDate) : true;
                      }}
                      success={startDateState === 'success'}
                      error={startDateState === 'error'}
                      labelText='Start Date'
                      id='startdate'
                      validator={isStartDateValid}
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        value: startDate,
                        onChange: (event) => {
                          setDirtyData(true);
                          setStartDateState('success');
                          setStartDate(event._d);
                        },
                      }}
                    />
                  </GridItem>
                  <GridItem xs={6}>
                    <DatePicker
                      isValidDate={(current) => {
                        return startDate ? current.isAfter(startDate) : true;
                      }}
                      success={endDateState === 'success'}
                      error={endDateState === 'error'}
                      labelText='End Date'
                      id='enddate'
                      formControlProps={{
                        fullWidth: true,
                      }}
                      validator={isEndDateValid}
                      inputProps={{
                        value: endDate,
                        onChange: (event) => {
                          setDirtyData(true);
                          setEndDateState('success');
                          setEndDate(event._d);
                        },
                      }}
                    />
                  </GridItem>
                </GridContainer>
              </GridItem>
              <GridItem xs={6}>
                <CustomInput
                  helperText={
                    referenceState === 'error'
                      ? referenceError ?? 'An error has occurred'
                      : false
                  }
                  success={referenceState === 'success'}
                  error={referenceState === 'error'}
                  labelText='Transaction/Order ID'
                  id='reference'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    defaultValue: reference,
                    onChange: (event) => {
                      if (
                        verifyLength(event.target.value, reference, 3, false)
                      ) {
                        setReferenceState('success');
                        setReferenceError('');
                      } else {
                        setReferenceState('error');
                        setReferenceError(
                          'Field must contain at least 3 alphanumeric or special characters'
                        );
                      }
                      setReference(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />
                <CustomInput
                  helperText={
                    mistIdState === 'error'
                      ? mistIdError ?? 'An error has occurred'
                      : false
                  }
                  success={mistIdState === 'success'}
                  error={mistIdState === 'error'}
                  labelText='MIST ID'
                  id='mistid'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    defaultValue: mistId,
                    onChange: (event) => {
                      if (verifyLength(event.target.value, mistId, 3, false)) {
                        setMistIdState('success');
                        setMistIdError('');
                      } else {
                        setMistIdState('error');
                        setMistIdError(
                          'Field must contain at least 3 alphanumeric or special characters'
                        );
                      }
                      setMistId(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />
                <CustomInput
                  helperText={
                    userIdState === 'error'
                      ? userIdError ?? 'An error has occurred'
                      : false
                  }
                  success={userIdState === 'success'}
                  error={userIdState === 'error'}
                  labelText='User ID'
                  id='userid'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    defaultValue: userId,
                    onChange: (event) => {
                      if (verifyLength(event.target.value, userId, 3, false)) {
                        setUserIdState('success');
                        setUserIdError('');
                      } else {
                        setUserIdState('error');
                        setUserIdError(
                          'Field must contain at least 3 alphanumeric or special characters'
                        );
                      }
                      setUserId(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />
                <FormControl variant='standard' component='fieldset'>
                  <RadioGroup
                    aria-label='sessionState'
                    name='sessionState'
                    value={sessionState}
                    onChange={sessionStateChangeHandler}
                  >
                    <FormControlLabel
                      value='completed'
                      control={<ThemedRadio />}
                      label='Show completed transactions'
                    />
                    <FormControlLabel
                      value='expired'
                      control={<ThemedRadio />}
                      label='Show expired session only'
                    />
                    <FormControlLabel
                      value='unreconciled'
                      control={<ThemedRadio />}
                      label='Show unreconciled payments only'
                    />
                  </RadioGroup>
                </FormControl>
              </GridItem>
              <GridItem xs={12}>
                <Button
                  color='rose'
                  onClick={onClickSearch}
                  disabled={!dirtyData}
                  className={classes.registerButton}
                >
                  Search
                </Button>
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>
      </GridItem>
      {transactions?.length > 0 ? (
        <GridItem xs={12}>
          <Card>
            <CardHeader color='rose' icon>
              <CardIcon color='rose'>
                <SearchResultsIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Transactions</h4>
            </CardHeader>
            <CardBody>
              <Table
                tableHead={[
                  'Transaction/Order ID',
                  'Type',
                  'Location',
                  'Workstation',
                  'User',
                  'Total',
                  'Date',
                  'Actions',
                ]}
                tableData={searchResults()}
              />
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component='div'
                count={resultCount}
                page={page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                SelectProps={{
                  inputProps: {
                    'aria-label': 'rows per page',
                  },
                  native: true,
                }}
              />
            </CardBody>
          </Card>
        </GridItem>
      ) : search ? (
        <GridContainer alignItems='center' justify='center'>
          <GridItem xs={12}>
            <Card>
              <CardHeader color='rose' icon>
                <CardIcon color='rose'>
                  <SearchResultsIcon />
                </CardIcon>
                <h4 className={classes.cardIconTitle}>Transactions</h4>
              </CardHeader>
              <CardBody alignItems='center' align='center'>
                <b>No records to display</b>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      ) : null}
      {viewReceipt === true && receiptStatus === 'success' ? (
        <Receipt
          open={viewReceipt && currentReceipt}
          handleClose={handleCloseReceipt}
          receipt={currentReceipt}
          serviceType={serviceType}
        />
      ) : null}
    </GridContainer>
  );
}
